import { createSlice } from '@reduxjs/toolkit';

// Local storage key
const CART_STORAGE_KEY = 'vidhifarm_cart';

// Load cart from local storage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const parsedCart = JSON.parse(savedCart);
      return {
        items: parsedCart.items || [],
        totalQuantity: parsedCart.totalQuantity || 0,
        totalAmount: parsedCart.totalAmount || 0,
        appliedCoupon: parsedCart.appliedCoupon || null,
        deliveryDate: parsedCart.deliveryDate || null,
        deliveryAddress: parsedCart.deliveryAddress || null,
        specialInstructions: parsedCart.specialInstructions || null,
      };
    }
  } catch (error) {
    console.error('Error loading cart from storage:', error);
  }
  return {
    items: [],
    totalQuantity: 0,
    totalAmount: 0,
    appliedCoupon: null,
    deliveryDate: null,
    deliveryAddress: null,
    specialInstructions: null,
  };
};

// Save cart to local storage
const saveCartToStorage = (state) => {
  try {
    const cartData = {
      items: state.items,
      totalQuantity: state.totalQuantity,
      totalAmount: state.totalAmount,
      appliedCoupon: state.appliedCoupon,
      deliveryDate: state.deliveryDate,
      deliveryAddress: state.deliveryAddress,
      specialInstructions: state.specialInstructions,
      lastUpdated: Date.now()
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
  } catch (error) {
    console.error('Error saving cart to storage:', error);
  }
};

const initialState = {
  ...loadCartFromStorage(),
  isLoading: false,
  error: null,
  isAuthenticated: false,
  isSyncing: false,
  syncError: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { product, quantity = 1 } = action.payload;
      
      // Validate product exists
      if (!product || !product.id) {
        console.error('Cannot add to cart: Invalid product', product);
        return;
      }
      
      const existingItem = state.items.find(item => item.id === product.id);
      
      if (existingItem) {
        existingItem.quantity += quantity;
        existingItem.totalPrice = existingItem.quantity * existingItem.price;
      } else {
        // Extract and process image URL properly
        let imageUrl = '';
        if (product.images) {
          if (Array.isArray(product.images) && product.images.length > 0) {
            imageUrl = product.images.find(img => img.isPrimary)?.url || product.images[0]?.url || product.images[0] || '';
          } else if (product.images.items && Array.isArray(product.images.items) && product.images.items.length > 0) {
            imageUrl = product.images.items.find(img => img.isPrimary)?.url || product.images.items[0]?.url || product.images.items[0] || '';
          }
        }
        if (!imageUrl && product.primaryImage?.url) {
          imageUrl = product.primaryImage.url;
        }
        
        // Process the URL to make it public if it's an S3 URL
        if (imageUrl) {
          const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
          const region = 'ap-south-1';
          
          if (imageUrl.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
            const urlParts = imageUrl.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
            const filePath = urlParts ? urlParts.split('?')[0] : '';
            imageUrl = `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
          }
        }
        
        const itemPrice = product.discountPrice || product.price || 0;
        
        state.items.push({
          id: product.id,
          name: product.name || 'Unknown Product',
          price: itemPrice,
          originalPrice: product.price || itemPrice,
          discountPrice: product.discountPrice,
          image: imageUrl,
          unit: product.unit || 'unit',
          maxQuantity: product.stock || product.stockQuantity || 100,
          quantity,
          totalPrice: itemPrice * quantity,
        });
      }
      
      cartSlice.caseReducers.calculateTotals(state);
      saveCartToStorage(state);
    },
    
    removeFromCart: (state, action) => {
      const productId = action.payload;
      state.items = state.items.filter(item => item.id !== productId);
      cartSlice.caseReducers.calculateTotals(state);
      saveCartToStorage(state);
    },
    
    updateQuantity: (state, action) => {
      const { productId, quantity } = action.payload;
      const item = state.items.find(item => item.id === productId);
      
      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter(item => item.id !== productId);
        } else {
          item.quantity = Math.min(quantity, item.maxQuantity);
          item.totalPrice = item.quantity * item.price;
        }
      }
      
      cartSlice.caseReducers.calculateTotals(state);
      saveCartToStorage(state);
    },
    
    clearCart: (state) => {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.appliedCoupon = null;
      state.deliveryDate = null;
      state.deliveryAddress = null;
      state.specialInstructions = null;
      saveCartToStorage(state);
    },
    
    calculateTotals: (state) => {
      state.totalQuantity = state.items.reduce((total, item) => total + item.quantity, 0);
      state.totalAmount = state.items.reduce((total, item) => total + item.totalPrice, 0);
    },
    
    applyCoupon: (state, action) => {
      const { discountAmount, couponCode } = action.payload;
      state.appliedCoupon = {
        code: couponCode,
        discountAmount,
      };
      saveCartToStorage(state);
    },
    
    removeCoupon: (state) => {
      state.appliedCoupon = null;
      saveCartToStorage(state);
    },
    
    setDeliveryDate: (state, action) => {
      state.deliveryDate = action.payload;
      saveCartToStorage(state);
    },
    
    setDeliveryAddress: (state, action) => {
      state.deliveryAddress = action.payload;
      saveCartToStorage(state);
    },
    
    setSpecialInstructions: (state, action) => {
      state.specialInstructions = action.payload;
      saveCartToStorage(state);
    },

    // Authentication and syncing actions
    setAuthenticationStatus: (state, action) => {
      state.isAuthenticated = action.payload;
    },

    startCartSync: (state) => {
      state.isSyncing = true;
      state.syncError = null;
    },

    cartSyncSuccess: (state, action) => {
      state.isSyncing = false;
      // Merge with database cart if provided
      if (action.payload) {
        const { items, appliedCoupon, deliveryDate, deliveryAddress, specialInstructions } = action.payload;
        if (items) state.items = items;
        if (appliedCoupon) state.appliedCoupon = appliedCoupon;
        if (deliveryDate) state.deliveryDate = deliveryDate;
        if (deliveryAddress) state.deliveryAddress = deliveryAddress;
        if (specialInstructions) state.specialInstructions = specialInstructions;
        cartSlice.caseReducers.calculateTotals(state);
        saveCartToStorage(state);
      }
    },

    cartSyncError: (state, action) => {
      state.isSyncing = false;
      state.syncError = action.payload;
    },

    mergeLocalCart: (state, action) => {
      const { localCart } = action.payload;
      if (localCart && localCart.items && localCart.items.length > 0) {
        // Merge local cart items with current cart
        localCart.items.forEach(localItem => {
          const existingItem = state.items.find(item => item.id === localItem.id);
          if (existingItem) {
            existingItem.quantity += localItem.quantity;
            existingItem.totalPrice = existingItem.quantity * existingItem.price;
          } else {
            state.items.push(localItem);
          }
        });
        cartSlice.caseReducers.calculateTotals(state);
        saveCartToStorage(state);
      }
    },

    clearLocalStorage: (state) => {
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
      } catch (error) {
        console.error('Error clearing cart from storage:', error);
      }
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  calculateTotals,
  applyCoupon,
  removeCoupon,
  setDeliveryDate,
  setDeliveryAddress,
  setSpecialInstructions,
  setAuthenticationStatus,
  startCartSync,
  cartSyncSuccess,
  cartSyncError,
  mergeLocalCart,
  clearLocalStorage,
} = cartSlice.actions;

export default cartSlice.reducer;

// Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotal = (state) => state.cart.totalAmount;
export const selectCartQuantity = (state) => state.cart.totalQuantity;
export const selectCartItemCount = (state) => state.cart.items.length;